import React from 'react';

/**
 * [GLASS TOOLTIP 2026-08-31, user] The glass background every in-SVG chart tooltip sits on —
 * "just like we have designed the tooltip i want the same in the complete application".
 *
 * SVG cannot backdrop-blur on its own, so this is a foreignObject carrying a real
 * `backdrop-filter`, the same surface language as the app's HTML tooltips (My Sales, New Sales,
 * the Inventory KPI box and the GlobalTooltip capsule are ALREADY this kind of glass — checked
 * 2026-08-31; the only solid holdout was Team Performance's chart tooltip). The chart's own
 * `<text>` elements render above this unchanged.
 *
 * Lives here as ONE component so the recipe cannot drift between pages. Used by
 * ProcessAnalysisPage (money flow, comparisons, combo trends, quadrant) and
 * TeamPerformancePage.
 */
/**
 * [ONE GLASS RECIPE 2026-09-01, user] "make the tooltip same all over the application."
 *
 * The surface values live HERE as exported constants, and both tooltip families read them: this
 * SVG foreignObject for in-chart tooltips, and the GlobalTooltip capsule that themes every
 * `title` attribute in the app. They were separate copies until now — the capsule had drifted to
 * a near-opaque 0.97/0.98 fill with a dimmer border, which is exactly the mismatch the user spotted
 * between a KPI pill's tooltip and the chart tooltip below it.
 *
 * ⛔ Do not inline these anywhere else. A second copy is how the two drifted the first time.
 */
/* [LIQUID-GLASS 2026-11-02, user] The tooltip surface follows the app's liquid glass (styles/liquidGlass.ts): rounder (14 px),
   a softer colour boost (the old 160 % amplified whatever sat behind), a slightly firmer tint so the words stay crisp, and a
   light line on the top edge + a soft reflection on the bottom one (in glassShadow). Every tooltip reads these, so they all
   changed together. */
export const GLASS_RADIUS = 14;
export const GLASS_BLUR = 'blur(20px) saturate(125%)';

export const glassBackground = (dark: boolean): string => (dark
  ? 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 30%, rgba(255,255,255,0) 55%), rgba(24,25,26,0.64)'
  : 'linear-gradient(180deg, rgba(255,255,255,0.80) 0%, rgba(255,255,255,0.20) 40%, rgba(255,255,255,0) 70%), rgba(250,251,250,0.68)');

/** The border COLOUR alone — a pointer's two edges need it without the `1px solid`. */
export const glassBorderColor = (dark: boolean): string => (dark
  ? 'rgba(255,255,255,0.16)'
  : 'rgba(22,25,23,0.10)');

export const glassBorder = (dark: boolean): string => `1px solid ${glassBorderColor(dark)}`;

export const glassShadow = (dark: boolean): string => (dark
  ? '0 12px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -1px 0 rgba(255,255,255,0.06)'
  : '0 12px 32px rgba(22,25,23,0.16), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(255,255,255,0.5)');

/**
 * Fill for a tooltip's little pointer (the sidebar capsule's diamond, the My Sales / New Sales
 * notch). The capsule's own background is a gradient over a translucent base and a rotated 8px
 * square cannot carry that gradient, so the pointer takes the base tone — the same approximation
 * those pointers already made against their own backgrounds.
 */
export const glassPointer = (dark: boolean): string => (dark
  ? 'rgba(24,25,26,0.64)'
  : 'rgba(250,251,250,0.68)'); /* [LIQUID-GLASS 2026-11-02] the new base tone */

/** Ink that reads on the glass in each theme. Shared so every capsule's text matches too. */
export const glassInk = (dark: boolean): string => (dark ? '#f1f5f9' : '#161917');

const TipGlass: React.FC<{ w: number; h: number; dark: boolean }> = ({ w, h, dark }) => (
  <foreignObject width={w} height={h} style={{ overflow: 'visible', pointerEvents: 'none' }}>
    <div style={{
      width: '100%', height: '100%', borderRadius: GLASS_RADIUS, boxSizing: 'border-box',
      background: glassBackground(dark),
      backdropFilter: GLASS_BLUR,
      WebkitBackdropFilter: GLASS_BLUR,
      border: glassBorder(dark),
      boxShadow: glassShadow(dark),
    }} />
  </foreignObject>
);

export default TipGlass;
